import { useEffect } from "react";
import { resolveMediaUrl } from "../../api";
import { removeFromCart, setCartQuantity } from "../../shop/cartStore";
import { formatPrice } from "../../shop/formatPrice";
import QuantityStepper from "./QuantityStepper";
import { IconBag, IconClose } from "./ShopIcons";

function CartDrawer({ open, lines, t, lang, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  const subtotal = lines.reduce((sum, line) => sum + line.lineTotalHuf, 0);
  const hasUnavailable = lines.some((line) => !line.available);
  const count = lines.reduce((sum, line) => sum + line.quantity, 0);

  return (
    <div className={open ? "cart-drawer is-open" : "cart-drawer"} aria-hidden={!open}>
      <div className="cart-drawer-backdrop" onClick={onClose} />
      <aside
        className="cart-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        inert={!open || undefined}
      >
        <header className="cart-drawer-head">
          <h2 id="cart-drawer-title">
            {t.cartTitle}
            {count > 0 ? <span>{t.itemsLabel(count)}</span> : null}
          </h2>
          <button type="button" className="shop-icon-btn" onClick={onClose} aria-label={t.close}>
            <IconClose />
          </button>
        </header>

        {lines.length === 0 ? (
          <div className="cart-drawer-empty">
            <IconBag className="cart-drawer-empty-icon" />
            <p>{t.cartEmpty}</p>
            <button type="button" className="shop-add" onClick={onClose}>
              {t.continueShopping}
            </button>
          </div>
        ) : (
          <>
            <ul className="cart-lines">
              {lines.map((line) => (
                <li key={line.productId} className={line.available ? "cart-line" : "cart-line is-unavailable"}>
                  <img
                    src={line.imageUrl ? resolveMediaUrl(line.imageUrl) : "/img/mizulogo.png"}
                    alt=""
                    className="cart-line-thumb"
                  />
                  <div className="cart-line-info">
                    <p className="cart-line-name">{line.name}</p>
                    <p className="cart-line-unit">
                      {formatPrice(line.priceHuf)}
                      {line.unitLabel ? ` / ${line.unitLabel}` : ""}
                    </p>
                    {line.available ? (
                      <QuantityStepper
                        size="sm"
                        value={line.quantity}
                        onChange={(next) => setCartQuantity(line.productId, next)}
                        t={t}
                      />
                    ) : (
                      <p className="cart-line-warning">{t.unavailable}</p>
                    )}
                  </div>
                  <div className="cart-line-side">
                    <span className="cart-line-total">{formatPrice(line.lineTotalHuf)}</span>
                    <button
                      type="button"
                      className="cart-line-remove"
                      onClick={() => removeFromCart(line.productId)}
                    >
                      {t.remove}
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <footer className="cart-drawer-foot">
              <div className="cart-drawer-subtotal">
                <span>{t.subtotal}</span>
                <strong>{formatPrice(subtotal)}</strong>
              </div>
              <p className="cart-drawer-note">{t.shippingNote}</p>
              {hasUnavailable ? <p className="cart-line-warning">{t.productUnavailable}</p> : null}
              <a
                className={hasUnavailable ? "shop-checkout-btn is-disabled" : "shop-checkout-btn"}
                href={`/webshop/checkout?lang=${lang}`}
                aria-disabled={hasUnavailable || undefined}
                onClick={(event) => {
                  if (hasUnavailable) event.preventDefault();
                }}
              >
                {t.checkout}
              </a>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}

export default CartDrawer;
